import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { SetOrderAsTestMutation } from '../data/mutations/setOrderAsTest.gql';

// Type for the mutation response
type SetOrderAsTestMutationResponse = {
  setOrderAsTest?: {
    __typename: 'TestOrderResponse' | 'InputValidationFailure';
    message?: string;
  };
};

export interface UseSetOrderAsTestParams {
  /**
   * Optional callback when marking succeeds
   */
  onSuccess?: (data: SetOrderAsTestMutationResponse) => void;
  /**
   * Optional callback when marking fails
   */
  onError?: (error: Error) => void;
  /**
   * Optional callback after mutation completes (success or error)
   */
  onCompleted?: (data: SetOrderAsTestMutationResponse) => void;
}

export interface SetOrderAsTestResult {
  success: boolean;
  message?: string;
}

/**
 * Hook to mark an order as a test order
 * Used for testing purposes to exclude orders from production data
 *
 * @param params - Configuration for callbacks
 * @returns setOrderAsTest function, loading state, and error
 *
 * @example
 * ```tsx
 * const { setOrderAsTest, loading, error } = useSetOrderAsTest({
 *   onSuccess: () => {
 *     showNotification('Order marked as test');
 *     refetchOrder();
 *   },
 *   onError: (error) => {
 *     showNotification(`Error: ${error.message}`, 'error');
 *   },
 * });
 *
 * const handleMarkAsTest = async () => {
 *   const result = await setOrderAsTest('order-123');
 *   if (result.success) {
 *     console.log('Successfully marked as test');
 *   }
 * };
 * ```
 */
export const useSetOrderAsTest = (params?: UseSetOrderAsTestParams) => {
  const { onSuccess, onError, onCompleted } = params || {};

  const [executeMutation, { loading, error }] =
    useMutation<SetOrderAsTestMutationResponse>(SetOrderAsTestMutation, {
      onCompleted: (data) => {
        onCompleted?.(data);
        const response = data?.setOrderAsTest;

        // Check response type to determine success
        if (response?.__typename === 'TestOrderResponse') {
          onSuccess?.(data);
        } else if (response?.__typename === 'InputValidationFailure') {
          const errorMessage = response.message || 'Validation failed';
          onError?.(new Error(errorMessage));
        }
      },
      onError,
    });

  /**
   * Mark an order as a test order
   *
   * @param orderId - The ID of the order to mark as test
   * @returns Result indicating success/failure
   */
  const setOrderAsTest = useCallback(
    async (orderId: string): Promise<SetOrderAsTestResult> => {
      try {
        const result = await executeMutation({
          variables: { orderId },
        });

        const response = result.data?.setOrderAsTest;

        if (response?.__typename === 'TestOrderResponse') {
          return {
            success: true,
            message: response.message || 'Order marked as test',
          };
        }
        if (response?.__typename === 'InputValidationFailure') {
          return {
            success: false,
            message: response.message || 'Validation failed',
          };
        }

        return {
          success: false,
          message: 'Unknown error occurred',
        };
      } catch (err) {
        return {
          success: false,
          message: err instanceof Error ? err.message : 'Unknown error',
        };
      }
    },
    [executeMutation]
  );

  return {
    setOrderAsTest,
    loading,
    error: error || null,
  };
};
